<template>
    <div id="account-search-form">
        <form class="search-page-form form"
              @submit.prevent="submit">
            <div class="hidden-xs alert alert-info">
                <i class="fa-solid fa-magnifying-glass"></i> {{ l('MANAGE.SEARCH_HELP') }}
            </div>

            <input id="account-search-input"
                   ref="account-search-input"
                   v-model="query"
                   v-auto-focus
                   v-select-on-focus
                   type="text"
                   class="form-control input-lg search-page-form__input"
                   :placeholder="searchInputPlaceholder"
                   @keyup.enter="submit" />

            <hr class="mt-2 mb-2" />

            <div class="flex flex--gap">
                <button type="button"
                        class="search-page-form__clear-btn btn btn-xl btn-default flex--no-grow"
                        :disabled="!clearButtonEnabled"
                        @click="clear()">
                    <i class="fa-regular fa-circle-xmark visible-xs"></i>
                    <span>{{ l('MANAGE.CLEAR') }}</span>
                </button>

                <!-- This is not disabled when the query is too short so that clicking it can explain why. -->
                <button type="submit"
                        class="search-page-form__search-btn btn btn-xl btn-primary flex--grow"
                        :class="{'visually-disabled': !isValidSearch}">
                    <span>{{ l('MANAGE.SEARCH') }}</span>
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { autoFocus } from '@/vue/directives/autoFocus';
import { blurElement, focusElement, selectTextInElement } from '@/functions/dom';
import { container } from '@/container';
import { selectOnFocus } from '@/vue/directives/selectOnFocus';

export default {
    name: 'AccountSearchForm',

    directives: {
        autoFocus,
        selectOnFocus,
    },

    props: {
        hasResults: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'clear',
        'submit',
    ],

    data() {
        return {
            minQueryLength: 3,

            query: '',
        };
    },

    computed: {
        searchInputPlaceholder() {
            return container.langService.getText('MANAGE.SEARCH_PLACEHOLDER');
        },

        isValidSearch() {
            return this.query.length >= this.minQueryLength;
        },

        clearButtonEnabled() {
            return !!this.query || this.hasResults;
        },
    },

    methods: {
        /**
         * @param {string} query
         */
        setFormData(query) {
            this.query = query || '';
        },

        submit() {
            if (!this.isValidSearch) {
                container.toastFactory.warningTop('MIN_QUERY_LENGTH_ERROR');
                return;
            }

            // The AccountSearchForm doesn't perform the search. It emits a submit event
            // for the parent component to consume.
            this.$emit('submit', this.query);
        },

        clear() {
            this.query = '';

            this.$emit('clear');

            this.focusSearchInput();
        },

        focusSearchInput(onMobile = true, onDesktop = true) {
            focusElement(this.$refs['account-search-input'], onMobile, onDesktop);
            selectTextInElement(this.$refs['account-search-input'], onMobile, onDesktop);
        },

        // This is mainly for Mobile devices, it removes the keyboard from taking up half the screen
        blurSearchInput(onMobile = true, onDesktop = true) {
            blurElement(this.$refs['account-search-input'], onMobile, onDesktop);
        },
    },
};
</script>
